/**
 * Parser for Structurizr DSL (https://docs.structurizr.com/dsl).
 *
 * Supported constructs:
 *   model block:
 *     id = person "Name" "Desc"
 *     id = softwareSystem "Name" "Desc" { ... }
 *     id = container "Name" "Desc" "Tech" { ... }
 *     id = component "Name" "Desc" "Tech"
 *     tags "Tag1" "Tag2"
 *     from -> to "label" "technology"
 *   views block:
 *     systemContext <id> { include * }
 *     container <id>    { include * }
 *     component <id>    { include * }
 */

// ─── C4 presets (matches useDrawing.js C4_PRESETS) ───────────────────────────

const C4_PRESETS = {
  'person':          { fill: '#083F88', stroke: '#073578', text: '#fff', w: 120, h: 130 },
  'external-person': { fill: '#6B6B6B', stroke: '#5C5C5C', text: '#fff', w: 120, h: 130 },
  'system':          { fill: '#1168BD', stroke: '#0E5AA7', text: '#fff', w: 220, h: 120 },
  'external-system': { fill: '#999999', stroke: '#8A8A8A', text: '#fff', w: 220, h: 120 },
  'container':       { fill: '#438DD5', stroke: '#3A7BB8', text: '#fff', w: 220, h: 120 },
  'database':        { fill: '#438DD5', stroke: '#3A7BB8', text: '#fff', w: 140, h: 130 },
  'queue':           { fill: '#438DD5', stroke: '#3A7BB8', text: '#fff', w: 220, h: 100 },
  'webapp':          { fill: '#438DD5', stroke: '#3A7BB8', text: '#fff', w: 220, h: 120 },
  'mobileapp':       { fill: '#438DD5', stroke: '#3A7BB8', text: '#fff', w: 140, h: 140 },
  'filestore':       { fill: '#438DD5', stroke: '#3A7BB8', text: '#fff', w: 220, h: 100 },
  'microservice':    { fill: '#438DD5', stroke: '#3A7BB8', text: '#fff', w: 200, h: 120 },
  'serverless':      { fill: '#438DD5', stroke: '#3A7BB8', text: '#fff', w: 200, h: 120 },
  'component':       { fill: '#85BBF0', stroke: '#78AAD4', text: '#000', w: 200, h: 100 },
  'controller':      { fill: '#85BBF0', stroke: '#78AAD4', text: '#000', w: 200, h: 100 },
  'repository':      { fill: '#85BBF0', stroke: '#78AAD4', text: '#000', w: 200, h: 100 },
  'service':         { fill: '#85BBF0', stroke: '#78AAD4', text: '#000', w: 200, h: 100 },
  'facade':          { fill: '#85BBF0', stroke: '#78AAD4', text: '#000', w: 200, h: 100 },
};

// ─── Text helpers ─────────────────────────────────────────────────────────────

const stripComments = (src) =>
  src.replace(/\/\*[\s\S]*?\*\//g, ' ').replace(/\/\/[^\n]*/g, '');

/** Extract all double-quoted strings from a text fragment. */
const parseStrings = (text) => {
  const out = [];
  const re = /"([^"]*)"/g;
  let m;
  while ((m = re.exec(text)) !== null) out.push(m[1]);
  return out;
};

// ─── Block extraction ─────────────────────────────────────────────────────────

/**
 * Starting from the first `{` at or after `startPos`, return the balanced
 * block content (everything inside the outer `{ ... }`).
 */
const extractBlock = (src, startPos) => {
  let depth = 0;
  let start = -1;
  for (let i = startPos; i < src.length; i++) {
    if (src[i] === '{') {
      if (depth === 0) start = i;
      depth++;
    } else if (src[i] === '}') {
      depth--;
      if (depth === 0 && start !== -1) {
        return { content: src.slice(start + 1, i), start, end: i };
      }
    }
  }
  return null;
};

/**
 * Find the first occurrence of `keywordRe` in `src` and extract its
 * immediately following `{ ... }` block.
 */
const findNamedBlock = (src, keywordRe) => {
  const m = keywordRe.exec(src);
  if (!m) return null;
  const braceIdx = src.indexOf('{', m.index + m[0].length - 1);
  if (braceIdx === -1) return null;
  return extractBlock(src, braceIdx);
};

// ─── Kind resolution ──────────────────────────────────────────────────────────

/**
 * Map Structurizr DSL element type + tags + technology to a C4 canvas kind.
 */
const resolveKind = (dslType, tags, technology, name) => {
  const tagSet = new Set((tags || []).map(t => t.toLowerCase().replace(/[\s-]/g, '')));
  const tech = (technology || '').toLowerCase();
  const n = (name || '').toLowerCase();

  if (dslType === 'person') {
    return tagSet.has('external') || n.includes('external') ? 'external-person' : 'person';
  }

  if (dslType === 'softwaresystem') {
    return tagSet.has('external') || n.includes('external') ? 'external-system' : 'system';
  }

  if (dslType === 'container') {
    if (tagSet.has('database') || /postgres|mysql|mongo|redis|oracle|sqlite|dynamo|cassandra|mssql/.test(tech)) return 'database';
    if (tagSet.has('queue') || tagSet.has('messagequeue') || /kafka|rabbit|sqs|activemq|nats|pubsub|servicebus/.test(tech)) return 'queue';
    if (tagSet.has('webapp') || tagSet.has('webapplication') || /react|angular|vue|browser|html|nginx|next\.?js/.test(tech)) return 'webapp';
    if (tagSet.has('mobileapp') || /ios|android|flutter|react native/.test(tech)) return 'mobileapp';
    if (tagSet.has('filestore') || tagSet.has('filestorage') || /s3|blob storage|gcs|file/.test(tech)) return 'filestore';
    if (tagSet.has('serverless') || /lambda|function app|cloud function|azure function/.test(tech)) return 'serverless';
    return 'container';
  }

  if (dslType === 'component') {
    if (n.includes('controller')) return 'controller';
    if (n.includes('repositor') || n.includes(' repo')) return 'repository';
    if (n.includes('service')) return 'service';
    if (n.includes('facade')) return 'facade';
    return 'component';
  }

  return 'system';
};

/** Build 3-line C4 text: name / [type: tech] / description */
const buildC4Text = (el) => {
  const lines = [el.name];
  const tech = el.tech ? `: ${el.tech}` : '';
  const typeLabel = {
    person:         '[Person]',
    softwaresystem: '[Software System]',
    container:      `[Container${tech}]`,
    component:      `[Component${tech}]`,
  }[el.dslType];
  if (typeLabel) lines.push(typeLabel);
  if (el.desc && el.desc.trim()) lines.push(el.desc.trim());
  return lines.join('\n');
};

// ─── Model parser ─────────────────────────────────────────────────────────────

/**
 * Recursively parse model block content into `elements` and `relations`.
 *
 * @param {string} src        - block content (without outer braces)
 * @param {string|null} parentId - DSL id of the enclosing element
 * @param {object} elements   - mutable map: dslId → element descriptor
 * @param {Array}  relations  - mutable array of { from, to, label, tech }
 */
const parseModelContent = (src, parentId, elements, relations) => {
  let pos = 0;

  while (pos < src.length) {
    // Skip leading whitespace / newlines
    while (pos < src.length && /[ \t\r\n]/.test(src[pos])) pos++;
    if (pos >= src.length) break;

    // Scan to end-of-line OR opening brace (whichever comes first)
    let lineEnd = pos;
    while (lineEnd < src.length && src[lineEnd] !== '\n' && src[lineEnd] !== '{') lineEnd++;
    const nextChar = lineEnd < src.length ? src[lineEnd] : null;
    const line = src.slice(pos, lineEnd).trim();

    if (!line || line === '}') { pos = lineEnd + 1; continue; }

    // ── tags "Tag1" "Tag2"
    if (/^tags\b/i.test(line)) {
      if (parentId && elements[parentId]) {
        elements[parentId].tags.push(...parseStrings(line));
      }
      pos = lineEnd + 1;
      continue;
    }

    // ── description "..."
    if (/^description\s+"/i.test(line)) {
      const strs = parseStrings(line);
      if (parentId && elements[parentId] && strs[0]) elements[parentId].desc = strs[0];
      pos = lineEnd + 1;
      continue;
    }

    // ── from -> to "label" "tech"  (IDs may be dotted: ss.wa -> ss.db)
    if (line.includes('->')) {
      const relMatch = line.match(/^([\w.]+)\s*->\s*([\w.]+)\s*(.*)/);
      if (relMatch) {
        const strs = parseStrings(relMatch[3] || '');
        relations.push({ from: relMatch[1], to: relMatch[2], label: strs[0] || '', tech: strs[1] || '' });
        // Relation can optionally have a block (tags/properties) — skip it
        if (nextChar === '{') {
          const block = extractBlock(src, lineEnd);
          pos = block ? block.end + 1 : lineEnd + 1;
        } else {
          pos = lineEnd + 1;
        }
        continue;
      }
    }

    // ── [id =] person|softwareSystem|container|component "name" ["desc"] ["tech"]
    const elemMatch = line.match(/^(?:(\w+)\s*=\s*)?(person|softwareSystem|container|component)\s*(.*)/i);
    if (elemMatch) {
      const dslId = elemMatch[1] || `_el_${Object.keys(elements).length}`;
      const dslType = elemMatch[2].toLowerCase();
      const rest = elemMatch[3].trim().replace(/\{[^}]*$/, '').trim(); // strip trailing {
      const strs = parseStrings(rest);
      const name = strs[0] || dslId;
      const desc = strs[1] || '';
      const tech = strs[2] || '';

      elements[dslId] = { dslType, name, desc, tech, tags: [], parentId, children: [] };
      if (parentId && elements[parentId]) elements[parentId].children.push(dslId);

      if (nextChar === '{') {
        const block = extractBlock(src, lineEnd);
        if (block) {
          parseModelContent(block.content, dslId, elements, relations);
          pos = block.end + 1;
        } else {
          pos = lineEnd + 1;
        }
      } else {
        pos = lineEnd + 1;
      }
      continue;
    }

    // Unknown statement — skip any attached block
    if (nextChar === '{') {
      const block = extractBlock(src, lineEnd);
      pos = block ? block.end + 1 : lineEnd + 1;
    } else {
      pos = lineEnd + 1;
    }
  }
};

// ─── Views parser ─────────────────────────────────────────────────────────────

const parseViews = (src) => {
  const views = [];
  let pos = 0;

  while (pos < src.length) {
    while (pos < src.length && /[ \t\r\n]/.test(src[pos])) pos++;
    if (pos >= src.length) break;

    let lineEnd = pos;
    while (lineEnd < src.length && src[lineEnd] !== '\n' && src[lineEnd] !== '{') lineEnd++;
    const nextChar = lineEnd < src.length ? src[lineEnd] : null;
    const line = src.slice(pos, lineEnd).trim();

    if (!line || line === '}') { pos = lineEnd + 1; continue; }

    // systemContext|container|component|dynamic|deployment
    const viewMatch = line.match(/^(systemContext|container|component|dynamic|deployment|filtered|custom)\b\s*(.*)/i);
    if (viewMatch) {
      const viewType = viewMatch[1].toLowerCase();
      const rest = viewMatch[2] || '';
      const idMatch = rest.match(/^(\w+|\*)/);
      const focusId = (idMatch && idMatch[1] !== '*') ? idMatch[1] : null;
      const strs = parseStrings(rest);
      const key = strs[0] || viewType;

      let includeIds = [];
      if (nextChar === '{') {
        const block = extractBlock(src, lineEnd);
        if (block) {
          const re = /\binclude\s+(.+)/gi;
          let m;
          while ((m = re.exec(block.content)) !== null) {
            const ids = m[1].trim().split(/[\s,]+/).filter(id => id && id !== '*' && id !== '->');
            includeIds.push(...ids);
          }
          pos = block.end + 1;
        } else {
          pos = lineEnd + 1;
        }
      } else {
        pos = lineEnd + 1;
      }

      views.push({ type: viewType, focusId, key, includeIds });
      continue;
    }

    if (nextChar === '{') {
      const block = extractBlock(src, lineEnd);
      pos = block ? block.end + 1 : lineEnd + 1;
    } else {
      pos = lineEnd + 1;
    }
  }

  return views;
};

// ─── Layout ───────────────────────────────────────────────────────────────────

const BASE_X  = 80;
const BASE_Y  = 80;
const GAP_X   = 300;  // horizontal gap between element columns
const GAP_Y   = 100;  // vertical gap between element rows
const MAX_INNER_COLS = 4;
/** Y-offset where container-view elements start (below system context rows). */
const CONTAINER_VIEW_BASE_Y = 440;

/** Width of a preset by kind; falls back to system if unknown. */
const presetW = (kind) => (C4_PRESETS[kind] || C4_PRESETS['system']).w;
const presetH = (kind) => (C4_PRESETS[kind] || C4_PRESETS['system']).h;
const kindOf  = (id, elements) =>
  resolveKind(elements[id]?.dslType, elements[id]?.tags, elements[id]?.tech, elements[id]?.name);

/**
 * Place element groups into columns, spacing each column by the max element
 * width in that column + GAP_X.  Returns { id → {x, y} }.
 * Row slot heights are shared across all groups so elements at the same row
 * index are vertically center-aligned regardless of their individual heights.
 */
const layoutColumnGroups = (groups, elements, baseX, baseY) => {
  const positions = {};
  const nonEmpty  = groups.filter(g => g.length > 0);
  if (!nonEmpty.length) return positions;

  // Shared row slot heights: max element height at each row index across all groups
  const numRows  = Math.max(...nonEmpty.map(g => g.length));
  const rowSlotH = Array.from({ length: numRows }, (_, r) =>
    Math.max(...nonEmpty.map(g => (r < g.length ? presetH(kindOf(g[r], elements)) : 0)))
  );
  const rowSlotY = [baseY];
  for (let r = 1; r < numRows; r++) rowSlotY[r] = rowSlotY[r - 1] + rowSlotH[r - 1] + GAP_Y;

  let curX = baseX;
  for (const group of groups) {
    if (!group.length) continue;
    const maxW = Math.max(...group.map(id => presetW(kindOf(id, elements))));
    group.forEach((id, i) => {
      const h = presetH(kindOf(id, elements));
      // Center element vertically within its shared row slot
      positions[id] = { x: curX, y: rowSlotY[i] + Math.round((rowSlotH[i] - h) / 2) };
    });
    curX += maxW + GAP_X;
  }
  return positions;
};

const layoutSystemContext = (visibleIds, elements) => {
  const kof      = (id) => kindOf(id, elements);
  const people   = visibleIds.filter(id => ['person', 'external-person'].includes(kof(id)));
  const internal = visibleIds.filter(id => kof(id) === 'system');
  const external = visibleIds.filter(id => kof(id) === 'external-system');
  const others   = visibleIds.filter(id =>
    !people.includes(id) && !internal.includes(id) && !external.includes(id));
  return layoutColumnGroups([people, internal, external, others], elements, BASE_X, BASE_Y);
};

// ─── Arrow edge helpers ───────────────────────────────────────────────────────

const opposeSide = (s) => ({ right: 'left', left: 'right', top: 'bottom', bottom: 'top' }[s]);

/**
 * Given the bounding boxes of source and target, return the best exit side
 * from the source element toward the target.
 */
const getBestSide = (fromPos, fromPreset, toPos, toPreset) => {
  const fx = fromPos.x + fromPreset.w / 2, fy = fromPos.y + fromPreset.h / 2;
  const tx = toPos.x  + toPreset.w  / 2,  ty = toPos.y  + toPreset.h  / 2;
  const dx = tx - fx, dy = ty - fy;
  return Math.abs(dx) >= Math.abs(dy)
    ? (dx > 0 ? 'right' : 'left')
    : (dy > 0 ? 'bottom' : 'top');
};

/** Center point of the named edge of a positioned element. */
const edgePt = (pos, preset, side) => {
  const { x, y } = pos, { w, h } = preset;
  return { right: { x: x + w, y: y + h / 2 }, left: { x, y: y + h / 2 },
           bottom: { x: x + w / 2, y: y + h }, top:  { x: x + w / 2, y } }[side]
    || { x: x + w / 2, y: y + h / 2 };
};

// ─── Canvas element builder ───────────────────────────────────────────────────

/**
 * Build all canvas elements for every view level in the parsed model.
 *
 * Pass 1 – system context (contextId: null):   persons + software systems
 * Pass 2 – container level (contextId: systemRectId): containers inside each system
 * Pass 3 – derive system-context arrows from container-level relations
 * Pass 4 – arrows in the correct context
 */
const buildCanvasElements = (elements, relations, views, ElementFactory) => {
  const allEls  = [];
  /** dslId → { rect, preset, pos } */
  const idToRect = {};
  /** rectId → { varName, fullPath, kind, label } */
  const elementMap = new Map();

  // ── Pass 1: system context ──────────────────────────────────────────────────
  const rootIds  = Object.keys(elements).filter(id => !elements[id].parentId);
  const rootPosMap = layoutSystemContext(rootIds, elements);
  const rootIdSet  = new Set(rootIds);

  for (const id of rootIds) {
    const pos = rootPosMap[id];
    if (!pos) continue;
    const el     = elements[id];
    const kind   = resolveKind(el.dslType, el.tags, el.tech, el.name);
    const preset = C4_PRESETS[kind] || C4_PRESETS['system'];

    const rect = ElementFactory.rectangle(pos.x, pos.y, preset.w, preset.h, {
      fill: preset.fill, stroke: preset.stroke, strokeWidth: 2, borderRadius: 8,
      c4: { kind, contextId: null },
      defaultLabel: el.name,
    });
    rect.textColor = preset.text;
    idToRect[id] = { rect, preset, pos };
    elementMap.set(rect.id, { varName: id, fullPath: id, kind, label: el.name });
    allEls.push(rect);

    const isPerson1 = kind === 'person' || kind === 'external-person';
    const headR1 = isPerson1 ? Math.round(preset.w * 0.22) : 0;
    const textY1 = isPerson1
      ? (pos.y + headR1 + 2 + headR1 + 10 + pos.y + preset.h) / 2
      : pos.y + preset.h / 2;
    const textEl = ElementFactory.text(pos.x + preset.w / 2, textY1, buildC4Text(el));
    textEl.attachedToElement = rect.id;
    textEl.fill = preset.text;
    textEl.fontSize = 13;
    textEl.c4   = { contextId: null };
    allEls.push(textEl);
  }

  // ── Pass 2: container level ─────────────────────────────────────────────────
  const containerViews = views.filter(v => v.type === 'container' && v.focusId);
  let   containerBaseY  = CONTAINER_VIEW_BASE_Y;
  /** All individual container rects — used in Pass 2.5 to build component-view shells. */
  const allContainerRects = [];

  for (const view of containerViews) {
    const focusEntry = idToRect[view.focusId];
    if (!focusEntry) continue;
    const contextId    = focusEntry.rect.id;
    const containerIds = elements[view.focusId]?.children || [];
    if (!containerIds.length) continue;

    // Width-aware grid: column widths derived from element presets
    const cols = Math.min(containerIds.length, MAX_INNER_COLS);
    const colW = Array.from({ length: cols }, (_, c) =>
      Math.max(...containerIds.filter((_, i) => i % cols === c)
        .map(id => { const k = resolveKind(elements[id].dslType, elements[id].tags, elements[id].tech, elements[id].name); return presetW(k); }))
    );
    const colX = [];
    let cx = BASE_X;
    for (let c = 0; c < cols; c++) { colX[c] = cx; cx += colW[c] + GAP_X; }

    const numRows = Math.ceil(containerIds.length / cols);
    const rowH = Array.from({ length: numRows }, (_, r) =>
      Math.max(...containerIds.filter((_, i) => Math.floor(i / cols) === r)
        .map(id => { const k = resolveKind(elements[id].dslType, elements[id].tags, elements[id].tech, elements[id].name); return presetH(k); }))
    );
    const rowY = [containerBaseY];
    for (let r = 1; r < numRows; r++) rowY[r] = rowY[r - 1] + rowH[r - 1] + GAP_Y;

    // Track insertion index so the boundary rect can be spliced before the containers
    const containerStartIdx = allEls.length;

    containerIds.forEach((id, i) => {
      const el     = elements[id];
      const kind   = resolveKind(el.dslType, el.tags, el.tech, el.name);
      const preset = C4_PRESETS[kind] || C4_PRESETS['container'];
      const pos    = { x: colX[i % cols], y: rowY[Math.floor(i / cols)] };

      const rect = ElementFactory.rectangle(pos.x, pos.y, preset.w, preset.h, {
        fill: preset.fill, stroke: preset.stroke, strokeWidth: 2, borderRadius: 8,
        c4: { kind, contextId },
        defaultLabel: el.name,
      });
      rect.textColor = preset.text;
      idToRect[id]   = { rect, preset, pos };
      elementMap.set(rect.id, { varName: id, fullPath: view.focusId + '.' + id, kind, label: el.name });
      allEls.push(rect);
      allContainerRects.push({ rect, el }); // collect for Pass 2.5

      const textEl = ElementFactory.text(pos.x + preset.w / 2, pos.y + preset.h / 2, buildC4Text(el));
      textEl.attachedToElement = rect.id;
      textEl.fill = preset.text;
      textEl.fontSize = 13;
      textEl.c4   = { contextId };
      allEls.push(textEl);
    });

    // ── Boundary box: dashed outline that wraps all containers ──────────────
    const PAD = 60;
    let bMinX = Infinity, bMinY = Infinity, bMaxX = -Infinity, bMaxY = -Infinity;
    containerIds.forEach(id => {
      const { pos } = idToRect[id];
      const k = kindOf(id, elements);
      bMinX = Math.min(bMinX, pos.x);
      bMinY = Math.min(bMinY, pos.y);
      bMaxX = Math.max(bMaxX, pos.x + presetW(k));
      bMaxY = Math.max(bMaxY, pos.y + presetH(k));
    });
    const bX = bMinX - PAD, bY = bMinY - PAD;
    const bW = bMaxX - bMinX + 2 * PAD;
    const bH = bMaxY - bMinY + 2 * PAD;

    const focusEl = elements[view.focusId];
    const boundaryRect = ElementFactory.rectangle(bX, bY, bW, bH, {
      fill: 'none', stroke: '#6b7280', strokeWidth: 1.5,
      borderRadius: 6, strokeDasharray: '8 4',
      c4: { contextId, isBoundary: true },
    });

    // Label at bottom-left of boundary (left-aligned standalone text)
    const boundaryLabel = ElementFactory.text(bX + 10, bY + bH - 14,
      `${focusEl?.name || 'System'} [Software System]`);
    boundaryLabel.fontSize = 11;
    boundaryLabel.fill = '#6b7280';
    boundaryLabel.textAnchor = 'start';
    boundaryLabel.c4 = { contextId };

    // Insert boundary before containers so it renders behind them (SVG doc order)
    allEls.splice(containerStartIdx, 0, boundaryRect, boundaryLabel);

    containerBaseY = bY + bH + GAP_Y + 40;
  }

  // ── Pass 2.5: component-view boundary shells for every container ────────────
  // When the user drills into a container (Component View), these provide the
  // dashed boundary box that represents the container itself in that view.
  // Components (if any) would have c4.contextId = containerRectId and appear
  // inside this boundary.
  {
    const CV_PAD = 60;
    const CV_W   = 400;
    const CV_H   = 280;
    let cvY = containerBaseY; // place below all container views in canvas space

    for (const { rect, el } of allContainerRects) {
      const cvContextId = rect.id;
      const bX = BASE_X - CV_PAD;

      const cvBoundaryRect = ElementFactory.rectangle(bX, cvY, CV_W + 2 * CV_PAD, CV_H, {
        fill: 'none', stroke: '#6b7280', strokeWidth: 1.5,
        borderRadius: 6, strokeDasharray: '8 4',
        c4: { contextId: cvContextId, isBoundary: true },
      });

      const cvBoundaryLabel = ElementFactory.text(bX + 10, cvY + CV_H - 14,
        `${el.name} [Container]`);
      cvBoundaryLabel.fontSize = 11;
      cvBoundaryLabel.fill = '#6b7280';
      cvBoundaryLabel.textAnchor = 'start';
      cvBoundaryLabel.c4 = { contextId: cvContextId };

      allEls.push(cvBoundaryRect, cvBoundaryLabel);
      cvY += CV_H + GAP_Y + 40;
    }
  }

  // ── Pass 3: derive system-context-level arrows ──────────────────────────────
  // e.g. u -> ss.wa  ⟹  u -> ss at the root level
  const syntheticRootRels = [];
  const seenRootRelKeys   = new Set();
  for (const rel of relations) {
    const fromParent = elements[rel.from]?.parentId ?? null;
    const toParent   = elements[rel.to]?.parentId   ?? null;
    const fromRoot   = fromParent ?? rel.from;
    const toRoot     = toParent   ?? rel.to;
    if (fromRoot !== toRoot && rootIdSet.has(fromRoot) && rootIdSet.has(toRoot)) {
      const key = `${fromRoot}→${toRoot}`;
      if (!seenRootRelKeys.has(key)) {
        seenRootRelKeys.add(key);
        syntheticRootRels.push({ from: fromRoot, to: toRoot, label: rel.label, tech: rel.tech });
      }
    }
  }

  // ── Pass 4: arrows ─────────────────────────────────────────────────────────
  const emitArrow = (rel, arrowContextId) => {
    const fromEntry = idToRect[rel.from];
    const toEntry   = idToRect[rel.to];
    if (!fromEntry || !toEntry) return;

    // Compute best exit/entry sides and use edge midpoints (not element centers)
    const fromSide = getBestSide(fromEntry.pos, fromEntry.preset, toEntry.pos, toEntry.preset);
    const toSide   = opposeSide(fromSide);
    const p1 = edgePt(fromEntry.pos, fromEntry.preset, fromSide);
    const p2 = edgePt(toEntry.pos,   toEntry.preset,   toSide);

    const arrow = ElementFactory.arrow(p1.x, p1.y, p2.x, p2.y);
    arrow.endCap = 'arrow';
    arrow.c4     = { contextId: arrowContextId };
    // Wire up connection tracking so arrows follow elements when they move
    arrow.startConnection = { elementId: fromEntry.rect.id, side: fromSide };
    arrow.endConnection   = { elementId: toEntry.rect.id,   side: toSide };
    allEls.push(arrow);

    if (rel.label || rel.tech) {
      const isHorizontal = fromSide === 'left' || fromSide === 'right';
      const mx = (p1.x + p2.x) / 2;
      const my = (p1.y + p2.y) / 2;
      // Labels above horizontal arrows, to the right of vertical arrows
      const lx = isHorizontal ? mx : mx + 16;
      const ly = isHorizontal ? my - 20 : my;

      if (rel.label) {
        const lbl = ElementFactory.text(lx, ly, rel.label);
        lbl.fontSize = 12;
        lbl.c4 = { contextId: arrowContextId };
        lbl.attachedToArrow = arrow.id;
        allEls.push(lbl);
      }
      if (rel.tech) {
        const tch = ElementFactory.text(lx, ly + (rel.label ? 14 : 0), `[${rel.tech}]`);
        tch.fontSize = 11;
        tch.fill = '#888888';
        tch.c4 = { contextId: arrowContextId };
        tch.attachedToArrow = arrow.id;
        allEls.push(tch);
      }
    }
  };

  // Explicit relations
  for (const rel of relations) {
    if (!idToRect[rel.from] || !idToRect[rel.to]) continue;
    const fromParent = elements[rel.from]?.parentId ?? null;
    const toParent   = elements[rel.to]?.parentId   ?? null;

    let arrowContextId;
    if (!fromParent && !toParent) {
      arrowContextId = null;
    } else {
      const parentId = fromParent || toParent;
      arrowContextId = idToRect[parentId]?.rect.id ?? null;
    }
    emitArrow(rel, arrowContextId);
  }

  // Inferred root-level relations
  for (const rel of syntheticRootRels) {
    emitArrow(rel, null);
  }

  return { elements: allEls, elementMap };
};

// ─── Public API ───────────────────────────────────────────────────────────────

/** Returns true when `code` looks like Structurizr DSL. */
export const isStructurizrDslCode = (code) =>
  /^\s*workspace\b/im.test(code);

/**
 * Parse Structurizr DSL and return canvas elements ready to render.
 *
 * @param {string} text          - raw DSL source
 * @param {object} ElementFactory - factory from createThemedElementFactory
 * @returns {Array} canvas elements
 */
export const parseStructurizrDsl = (text, ElementFactory) => {
  const src = stripComments(text || '');

  // Find workspace { ... }.
  // If the opening `{` is missing from the workspace declaration (common copy-paste
  // truncation), findNamedBlock grabs the wrong block — detect this and fall back
  // to treating the full source as workspace content.
  let workspaceContent;
  const workspaceBlock = findNamedBlock(src, /\bworkspace\b/i);
  if (workspaceBlock && /\b(model|views)\b/i.test(workspaceBlock.content)) {
    workspaceContent = workspaceBlock.content;
  } else {
    // Missing `{` on workspace line — strip the first line and use the rest
    workspaceContent = src.replace(/^[^\n]*\n/, '');
  }

  // Find model { ... }
  const modelBlock = findNamedBlock(workspaceContent, /\bmodel\b/i);
  if (!modelBlock) return { elements: [], elementMap: new Map() };

  const elements  = {};
  const relations = [];
  parseModelContent(modelBlock.content, null, elements, relations);

  // Resolve hierarchical IDs (e.g. !identifiers hierarchical: ss.wa → wa)
  const aliasMap = {};
  for (const [id, el] of Object.entries(elements)) {
    aliasMap[id] = id;
    if (el.parentId) aliasMap[`${el.parentId}.${id}`] = id;
  }
  const resolveId = (id) => aliasMap[id] ?? id;
  for (const rel of relations) {
    rel.from = resolveId(rel.from);
    rel.to   = resolveId(rel.to);
  }

  // Find views { ... } (optional)
  const viewsBlock = findNamedBlock(workspaceContent, /\bviews\b/i);
  const views = viewsBlock ? parseViews(viewsBlock.content) : [];

  const result = buildCanvasElements(elements, relations, views, ElementFactory);
  return { elements: result.elements, elementMap: result.elementMap };
};

// ─── C4 kind → DSL keyword mapping ────────────────────────────────────────────

const KIND_TO_DSL = {
  'person':          'person',
  'external-person': 'person',
  'system':          'softwareSystem',
  'external-system': 'softwareSystem',
  'container':       'container',
  'webapp':          'container',
  'database':        'container',   // DSL: container + tags "database"
  'queue':           'container',
  'mobileapp':       'container',
  'filestore':       'container',
  'microservice':    'container',
  'serverless':      'container',
  'component':       'component',
  'controller':      'component',
  'repository':      'component',
  'service':         'component',
  'facade':          'component',
};

const EXTERNAL_KINDS = new Set(['external-person', 'external-system']);
const DB_LIKE_KINDS  = new Set(['database', 'queue', 'filestore']);

/** Convert "Web Application" → "web_application" */
const nameToVar = (name) =>
  (name || 'element').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'element';

/** Extract C4 text lines from an elements array for a given rect id. */
const getTextLines = (elements, rectId) => {
  const textEl = elements.find(e => e.type === 'text' && e.attachedToElement === rectId);
  if (!textEl) return ['', '', ''];
  const parts = (textEl.content || '').split('\n');
  return [parts[0] || '', parts[1] || '', parts[2] || ''];
};

/** Extract technology from "[Type: Tech]" or "[Type]" line. */
const parseTechLine = (line) => {
  const m = line.match(/\[(?:[^:]+):\s*(.+?)\]/);
  return m ? m[1].trim() : '';
};

/**
 * Reconstruct Structurizr DSL from canvas elements.
 *
 * @param {Array}  elements   - canvas elements array
 * @param {Map}    elementMap - C4ElementMap (rectId → { varName, fullPath, kind, label })
 * @param {string} [diagramName] - workspace name
 * @returns {string|null} DSL string, or null if no C4 elements found
 */
export const canvasToStructurizrDsl = (elements, elementMap, diagramName = 'Workspace') => {
  const c4Rects = elements.filter(e => e.type === 'rectangle' && e.c4);
  if (!c4Rects.length) return null;

  // Build a local var registry to guarantee uniqueness for new elements
  const usedVars = new Set([...elementMap.values()].map(e => e.varName));
  const assignVar = (name) => {
    let v = nameToVar(name);
    let n = 2;
    while (usedVars.has(v)) v = nameToVar(name) + '_' + n++;
    usedVars.add(v);
    return v;
  };

  // Helper: get or create an element-map entry for a rect
  const getEntry = (rect) => {
    if (elementMap.has(rect.id)) return elementMap.get(rect.id);
    const [name] = getTextLines(elements, rect.id);
    const kind    = rect.c4?.kind || 'system';
    const varName = assignVar(name || kind);
    return { varName, fullPath: varName, kind, label: name };
  };

  const lines = [];
  const ind   = (n) => '  '.repeat(n);

  lines.push(`workspace "${diagramName}" {`);
  lines.push(`${ind(1)}model {`);

  // ── Root elements ────────────────────────────────────────────────────────────
  const rootRects = c4Rects.filter(r => r.c4.contextId === null);
  for (const rect of rootRects) {
    const [name, techLine, desc] = getTextLines(elements, rect.id);
    const entry = getEntry(rect);
    const dslKw = KIND_TO_DSL[entry.kind] || KIND_TO_DSL[rect.c4.kind] || 'softwareSystem';
    const tech  = parseTechLine(techLine);
    const isExt = EXTERNAL_KINDS.has(rect.c4.kind);
    const isDb  = DB_LIKE_KINDS.has(rect.c4.kind);

    // Find children (containers whose contextId === this rect's id)
    const children = c4Rects.filter(r => r.c4.contextId === rect.id);

    const descStr  = desc  ? ` "${desc}"`  : '';
    const techStr  = tech  ? ` "${tech}"`  : '';

    if (!children.length && !isExt && !isDb) {
      lines.push(`${ind(2)}${entry.varName} = ${dslKw} "${name}"${descStr}`);
    } else {
      lines.push(`${ind(2)}${entry.varName} = ${dslKw} "${name}" {`);
      if (desc)  lines.push(`${ind(3)}description "${desc}"`);
      if (tech)  lines.push(`${ind(3)}technology "${tech}"`);
      if (isExt) lines.push(`${ind(3)}tags "external"`);
      if (isDb)  lines.push(`${ind(3)}tags "database"`);

      for (const child of children) {
        const [cName, cTechLine, cDesc] = getTextLines(elements, child.id);
        const cEntry  = getEntry(child);
        const cDslKw  = KIND_TO_DSL[cEntry.kind] || KIND_TO_DSL[child.c4.kind] || 'container';
        const cTech   = parseTechLine(cTechLine);
        const cIsDb   = DB_LIKE_KINDS.has(child.c4.kind);
        const cDescStr = cDesc ? ` "${cDesc}"` : '';
        const cTechStr = cTech ? ` "${cTech}"` : '';

        // Check for grandchildren (components)
        const grandChildren = c4Rects.filter(r => r.c4.contextId === child.id);
        if (!grandChildren.length && !cIsDb) {
          lines.push(`${ind(3)}${cEntry.varName} = ${cDslKw} "${cName}"${cDescStr}${cTechStr}`);
        } else {
          lines.push(`${ind(3)}${cEntry.varName} = ${cDslKw} "${cName}" {`);
          if (cDesc) lines.push(`${ind(4)}description "${cDesc}"`);
          if (cTech) lines.push(`${ind(4)}technology "${cTech}"`);
          if (cIsDb) lines.push(`${ind(4)}tags "database"`);
          for (const gc of grandChildren) {
            const [gcName, gcTechLine, gcDesc] = getTextLines(elements, gc.id);
            const gcEntry = getEntry(gc);
            const gcDslKw = KIND_TO_DSL[gcEntry.kind] || 'component';
            const gcTech  = parseTechLine(gcTechLine);
            const gcDescStr = gcDesc ? ` "${gcDesc}"` : '';
            const gcTechStr = gcTech ? ` "${gcTech}"` : '';
            lines.push(`${ind(4)}${gcEntry.varName} = ${gcDslKw} "${gcName}"${gcDescStr}${gcTechStr}`);
          }
          lines.push(`${ind(3)}}`);
        }
      }

      lines.push(`${ind(2)}}`);
    }
  }

  // ── Relations ────────────────────────────────────────────────────────────────
  const arrows = elements.filter(e => e.type === 'arrow' && e.c4);
  for (const arrow of arrows) {
    // Canvas stores connections as startConnection.elementId; tests use startElementId directly.
    const srcId = arrow.startConnection?.elementId ?? arrow.startElementId;
    const tgtId = arrow.endConnection?.elementId   ?? arrow.endElementId;
    const srcRect = c4Rects.find(r => r.id === srcId);
    const tgtRect = c4Rects.find(r => r.id === tgtId);
    const srcEntry = elementMap.get(srcId) ?? (srcRect ? getEntry(srcRect) : null);
    const tgtEntry = elementMap.get(tgtId) ?? (tgtRect ? getEntry(tgtRect) : null);
    if (!srcEntry || !tgtEntry) continue;

    // Arrow label from attached text or arrow content
    const label = arrow.content || '';
    const labelStr = label ? ` "${label}"` : '';
    lines.push(`${ind(2)}${srcEntry.fullPath} -> ${tgtEntry.fullPath}${labelStr}`);
  }

  lines.push(`${ind(1)}}`);
  lines.push('}');

  return lines.join('\n');
};
